import React from "react";
import { Helmet } from "react-helmet";

export const SITE_URL = "https://www.kleanleaf.us";
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/iPhone.webp`;

export const absoluteUrl = (value) => {
  if (!value) return DEFAULT_SOCIAL_IMAGE;
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

function Seo({
  title,
  description,
  path = "/",
  prevPath,
  nextPath,
  image,
  imageAlt = "Klean Leaf private label laundry detergent sheets",
  type = "website",
  schema = [],
}) {
  const canonical = `${SITE_URL}${path}`;
  const socialImage = absoluteUrl(image);
  const schemas = Array.isArray(schema) ? schema : [schema];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {prevPath && <link rel="prev" href={`${SITE_URL}${prevPath}`} />}
      {nextPath && <link rel="next" href={`${SITE_URL}${nextPath}`} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Klean Leaf" />
      <meta property="og:image" content={socialImage} />
      <meta property="og:image:secure_url" content={socialImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:image:alt" content={imageAlt} />
      {schemas.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness", "Manufacturer"],
  name: "Klean Leaf",
  url: SITE_URL,
  description:
    "Klean Leaf is a B2B private-label laundry detergent sheet manufacturer with a U.S. office and client support team in Columbus, Ohio and a manufacturing facility and factory team in China.",
  email: "hello@kleanleaf.us",
  telephone: "+1-614-906-5129",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Columbus",
    addressRegion: "OH",
    addressCountry: "US",
  },
  sameAs: [
    "https://www.facebook.com/profile.php?id=61591538740861",
    "https://www.instagram.com/kleanleaf",
  ],
  areaServed: ["United States", "North America"],
  knowsAbout: [
    "Private-label laundry detergent sheets",
    "Bulk laundry sheet manufacturing",
    "Custom scent development",
    "Formula development",
    "Packaging customization",
    "Refill shop bulk cartons",
    "Laundry sheet logistics support",
  ],
  makesOffer: [
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "Private-label laundry detergent sheet manufacturing",
        serviceType: "B2B private label manufacturing",
      },
    },
    {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: "Laundry sheet packaging customization",
        serviceType: "Custom packaging support",
      },
    },
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Klean Leaf",
  url: SITE_URL,
  inLanguage: "en-US",
  about: {
    "@type": "Organization",
    name: "Klean Leaf",
  },
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Private Label Laundry Detergent Sheets",
  brand: {
    "@type": "Brand",
    name: "Klean Leaf",
  },
  category: "Laundry detergent sheets",
  description:
    "Private label laundry detergent sheets with custom scent, formula, packaging, and bulk carton options for business buyers.",
  additionalProperty: [
    {
      "@type": "PropertyValue",
      name: "Retail packaged product MOQ",
      value: "MOQ starts at 1,000 retail package units per SKU.",
    },
    {
      "@type": "PropertyValue",
      name: "Lead time",
      value: "Around 35 to 45 days after order confirmation.",
    },
    {
      "@type": "PropertyValue",
      name: "Packaging options",
      value: "Paper bag, paper box, custom packaging, and bulk refill cartons.",
    },
    {
      "@type": "PropertyValue",
      name: "Available documents",
      value: "Documentation and testing support can vary by product, formula, and buyer requirements.",
    },
  ],
};

export const privateLabelServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Private-label laundry detergent sheet manufacturing",
  provider: {
    "@type": "Organization",
    name: "Klean Leaf",
    url: SITE_URL,
  },
  areaServed: "United States",
  serviceType: "B2B private label manufacturing",
  description:
    "Private-label laundry detergent sheet manufacturing with bulk production, custom scent and formula support, packaging customization, samples, refill shop bulk carton options, logistics support, and around 35 to 45 day lead time after order confirmation.",
  audience: {
    "@type": "BusinessAudience",
    audienceType:
      "Brands, wholesalers, refill shops, e-commerce brands, subscription brands, retailers, distributors, and eco-home product companies.",
  },
};

export default Seo;
